import React, { useState, useRef, useEffect, useCallback } from 'react';

const AutoDocumentAnnotator = ({ 
  imageUrl, 
  ocrData, 
  importantFields = {},
  onMatchesFound
}) => {
  const [matches, setMatches] = useState({});
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef(null);

  // Colors for different field types
  const fieldColors = {
    company_name: '#FF6B6B',
    invoice_no: '#4ECDC4',
    date: '#45B7D1',
    amount: '#96CEB4',
    tax_amount: '#FFEAA7',
    total_amount: '#DDA0DD',
    customer_name: '#98D8C8',
    address: '#F7DC6F',
    gstin: '#BB8FCE',
    hsn_code: '#85C1E9',
    default: '#007bff'
  };

  useEffect(() => {
    if (imageRef.current && imageLoaded) {
      const img = imageRef.current;
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    }
  }, [imageLoaded]);

  // Function to find text similarity
  const calculateSimilarity = useCallback((text1, text2) => {
    const clean1 = text1.toLowerCase().replace(/[^a-z0-9]/g, '');
    const clean2 = text2.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    // Exact match
    if (clean1 === clean2) return 1.0;
    
    // Contains match
    if (clean1.includes(clean2) || clean2.includes(clean1)) return 0.8;
    
    // Fuzzy matching for partial matches
    const longer = clean1.length > clean2.length ? clean1 : clean2;
    const shorter = clean1.length > clean2.length ? clean2 : clean1;
    
    if (longer.length === 0) return 0;
    
    let matches = 0;
    for (let i = 0; i < shorter.length; i++) {
      if (longer.includes(shorter[i])) matches++;
    }
    
    return matches / longer.length;
  }, []);

  // Function to find matches between important fields and OCR predictions
  const findMatches = useCallback(() => {
    const foundMatches = {};
    
    Object.entries(importantFields).forEach(([fieldName, fieldValue]) => {
      let bestMatch = null;
      let bestScore = 0;
      
      ocrData.predictions.forEach((prediction) => {
        const similarity = calculateSimilarity(prediction.class, fieldValue);
        
        // Threshold for considering a match (adjust as needed)
        if (similarity > 0.6 && similarity > bestScore) {
          bestMatch = {
            prediction,
            similarity,
            fieldName,
            fieldValue
          };
          bestScore = similarity;
        }
      });
      
      if (bestMatch) {
        foundMatches[fieldName] = bestMatch;
      }
    });
    
    setMatches(foundMatches);
    onMatchesFound && onMatchesFound(foundMatches);
  }, [ocrData, importantFields, calculateSimilarity, onMatchesFound]);

  useEffect(() => {
    if (ocrData && ocrData.predictions && Object.keys(importantFields).length > 0) {
      findMatches();
    }
  }, [ocrData, importantFields, findMatches]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const getScaledCoordinates = (box) => {
    if (!imageRef.current || !imageLoaded) return {};
    
    const img = imageRef.current;
    const scaleX = img.offsetWidth / imageDimensions.width;
    const scaleY = img.offsetHeight / imageDimensions.height;
    
    return {
      left: box.x * scaleX,
      top: box.y * scaleY,
      width: box.width * scaleX,
      height: box.height * scaleY
    };
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Auto Document Annotation</h2>
          <p className="text-sm text-gray-600 mt-1">
            Automatically highlighting important fields in the document
          </p>
        </div>
        
        <div className="p-4">
          {/* Image container with annotations */}
          <div className="relative inline-block border border-gray-200 rounded-lg overflow-hidden mb-6">
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Document with annotations"
              className="block max-w-full h-auto"
              onLoad={handleImageLoad}
            />
            
            {/* Overlay bounding boxes for matches */}
            {imageLoaded && Object.entries(matches).map(([fieldName, match]) => {
              const scaledCoords = getScaledCoordinates(match.prediction);
              const color = fieldColors[fieldName] || fieldColors.default;
              
              return (
                <div key={fieldName}>
                  {/* Bounding box */}
                  <div
                    className="absolute border-3 pointer-events-none"
                    style={{
                      left: scaledCoords.left,
                      top: scaledCoords.top,
                      width: scaledCoords.width,
                      height: scaledCoords.height,
                      borderColor: color,
                      backgroundColor: `${color}20`,
                      borderWidth: '3px'
                    }}
                  >
                    {/* Field label */}
                    <div 
                      className="absolute -top-8 left-0 px-3 py-1 text-xs font-bold text-white rounded-md shadow-lg whitespace-nowrap"
                      style={{ backgroundColor: color }}
                    >
                      {fieldName.replace('_', ' ').toUpperCase()}
                      <div className="text-xs font-normal opacity-90">
                        {(match.similarity * 100).toFixed(0)}% match
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Show unmatched predictions with light overlay */}
            {imageLoaded && ocrData && ocrData.predictions && ocrData.predictions
              .filter(pred => !Object.values(matches).find(match => match.prediction.detection_id === pred.detection_id))
              .map((prediction) => {
                const scaledCoords = getScaledCoordinates(prediction);
                
                return (
                  <div
                    key={prediction.detection_id}
                    className="absolute border border-gray-300 pointer-events-none opacity-30"
                    style={{
                      left: scaledCoords.left,
                      top: scaledCoords.top,
                      width: scaledCoords.width,
                      height: scaledCoords.height,
                      backgroundColor: 'rgba(128, 128, 128, 0.1)'
                    }}
                    title={`${prediction.class} (${(prediction.confidence * 100).toFixed(1)}%)`}
                  />
                );
              })}
          </div>

          {/* Results section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Fields */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Target Fields</h3>
              {Object.keys(importantFields).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(importantFields).map(([fieldName, fieldValue]) => (
                    <div key={fieldName} className="flex items-center p-2 bg-white rounded border">
                      <div 
                        className="w-4 h-4 rounded mr-3 flex-shrink-0"
                        style={{ backgroundColor: fieldColors[fieldName] || fieldColors.default }}
                      ></div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-700">{fieldName}:</div>
                        <div className="text-sm text-gray-600">{fieldValue}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No target fields provided</p>
              )}
            </div>

            {/* Match Results */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Found Matches</h3>
              {Object.keys(matches).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(matches).map(([fieldName, match]) => (
                    <div key={fieldName} className="p-2 bg-white rounded border">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center">
                          <div 
                            className="w-4 h-4 rounded mr-2"
                            style={{ backgroundColor: fieldColors[fieldName] || fieldColors.default }}
                          ></div>
                          <span className="font-medium text-gray-700">{fieldName}</span>
                        </div>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {(match.similarity * 100).toFixed(0)}% match
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 ml-6">
                        <div><strong>Target:</strong> {match.fieldValue}</div>
                        <div><strong>Found:</strong> {match.prediction.class}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No matches found</p>
              )}
            </div>
          </div>

          {/* Export matched data */}
          {Object.keys(matches).length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Extracted Data</h4>
              <pre className="bg-white p-3 rounded text-sm overflow-auto border">
{JSON.stringify(
  Object.fromEntries(
    Object.entries(matches).map(([fieldName, match]) => [
      fieldName, 
      match.prediction.class
    ])
  ), 
  null, 
  2
)}
              </pre>
              <button
                onClick={() => {
                  const extractedData = Object.fromEntries(
                    Object.entries(matches).map(([fieldName, match]) => [
                      fieldName, 
                      match.prediction.class
                    ])
                  );
                  navigator.clipboard.writeText(JSON.stringify(extractedData, null, 2));
                  alert('Extracted data copied to clipboard!');
                }}
                className="mt-3 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
              >
                Copy Extracted Data
              </button>
            </div>
          )}

          {/* Matching Statistics */}
          {Object.keys(importantFields).length > 0 && (
            <div className="mt-4 p-3 bg-gray-100 rounded text-sm">
              <strong>Matching Summary:</strong> {Object.keys(matches).length} out of {Object.keys(importantFields).length} fields matched
              {Object.keys(matches).length < Object.keys(importantFields).length && (
                <div className="text-orange-600 mt-1">
                  <strong>Missing fields:</strong> {Object.keys(importantFields).filter(field => !matches[field]).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoDocumentAnnotator;